<script lang="ts">
  import { Button, Image, Media, Markdown, Text } from '@lib/components';
  import { responsiveStyle, parseResponsiveCSS } from '@lib/directives';
  import { handleCtaNavigation } from '@lib/utils/navigation';
  import type { CardBlock, SocialLink } from './types';

  export let block: CardBlock;
  export let isFullscreen: boolean = false;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;

  $: layout = block.layout?.variant || 'image-top';
  $: imageStyle = block.layout?.imageStyle || 'rounded';
  $: hasMedia = block.media && (block.media.type === 'image' || block.media.type === 'video') && block.media.filename;
  // Convert CardMedia to Media format for the unified Media component
  $: mediaForComponent = block.media ? {
    ...block.media,
    // Add any missing properties expected by Media component
    ...(block.media.type === 'video' && {
      autoplay: (block.media as any).autoPlay !== false,
      muted: (block.media as any).mute !== false,
      loop: (block.media as any).loop !== false,
      controls: (block.media as any).controls || false
    })
  } : undefined;

  
  // Keep imageSrc for background images
  $: imageSrc = block.media?.filename ? `/images/${block.media.filename}` : ''
  
  // For now, we'll handle alignment purely through CSS and pass the original alignment
  // The CSS will override it for image-left on desktop only
  
  // Background processing
  $: hasBackground = block.background && (block.background.type === 'image' || block.background.type === 'gradient');
  $: shouldExtendBackground = block.background?.extend !== false; // Default to true for extending
  
  function getBackgroundStyle(bg: typeof block.background) {
    if (!bg) return '';
    
    if (bg.type === 'gradient') {
      const direction = bg.direction || '135deg';
      return `linear-gradient(${direction}, ${bg.from}, ${bg.to})`;
    }
    
    if (bg.type === 'image' && (bg.url || bg.filename)) {
      const imageUrl = bg.url || (bg.filename ? `/images/${bg.filename}` : '');
      const objectFit = bg.objectFit || 'cover';
      return `url('${imageUrl}') center / ${objectFit} no-repeat`;
    }
    
    return '';
  }
  
  // Create the style attributes based on whether background should extend
  $: backgroundStyle = getBackgroundStyle(block.background);
  $: cardStyle = hasBackground && shouldExtendBackground 
    ? `--card-background: ${backgroundStyle}`
    : hasBackground 
    ? `background: ${backgroundStyle}` 
    : '';
  
  // Parse CSS for responsive directive
  $: responsiveCSS = (block as any).css ? parseResponsiveCSS((block as any).css) : null;
  
  function getMaxWidth() {
    if (!block.layout?.width) return isFullscreen ? '600px' : '100%';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }
  
  function getThemeColor(color?: string) {
    if (!color) return undefined;
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
      'accent': 'var(--theme-accent-color)',
      'success': 'var(--theme-success-color)',
      'warning': 'var(--theme-warning-color)',
      'error': 'var(--theme-error-color)',
      'info': 'var(--theme-info-color)'
    };
    return themeColors[color] || color;
  }

  function getLinkUrl(link: SocialLink): string {
    if (link.platform === 'whatsapp') {
      const text = link.text ? `text=${encodeURIComponent(link.text)}` : '';
      if ('phone' in link && link.phone) {
        const phone = link.phone.replace(/[^\d+]/g, ''); // Keep digits and + for country code
        return `https://wa.me/${phone}${text ? '?' + text : ''}`;
      }
      // Share mode (no phone number)
      return `https://wa.me/?${text}`;
    }
    return 'url' in link ? link.url : '';
  }

  function getTextOnColor(color?: string) {
    if (!color) return undefined;
    const textColors: Record<string, string> = {
      'primary': 'var(--theme-text-on-primary)',
      'secondary': 'var(--theme-text-on-secondary)',
      'accent': 'var(--theme-text-on-accent, var(--theme-text-on-primary))',
      'success': 'var(--theme-text-on-success, white)',
      'warning': 'var(--theme-text-on-warning, white)',
      'error': 'var(--theme-text-on-error, white)',
      'info': 'var(--theme-text-on-info, white)'
    };
    return textColors[color] || undefined;
  }
  
  function getSocialIcon(platform: string): string {
    const icons: Record<string, string> = {
      linkedin: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>`,
      instagram: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>`,
      facebook: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`,
      youtube: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z"/></svg>`,
      tiktok: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-2.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z"/></svg>`,
      x: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.205 2.25h3.308l-7.227 8.26 8.502 11.24H16.13l-5.214-6.817L4.95 21.75H1.64l7.73-8.835L1.215 2.25H8.04l4.713 6.231zm-1.161 17.52h1.833L7.045 4.126H5.078z"/></svg>`,
      github: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z"/></svg>`,
      website: `<svg viewBox="1 1 22 22" fill="currentColor"><path d="M16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2m-5.15 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56M14.34 14H9.66c-.1-.66-.16-1.32-.16-2 0-.68.06-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2M12 19.96c-.83-1.2-1.5-2.53-1.91-3.96h3.82c-.41 1.43-1.08 2.76-1.91 3.96M8 8H5.08A7.923 7.923 0 0 1 9.4 4.44C8.8 5.55 8.35 6.75 8 8m-2.92 8H8c.35 1.25.8 2.45 1.4 3.56A8.008 8.008 0 0 1 5.08 16m-.82-2C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2M12 4.03c.83 1.2 1.5 2.54 1.91 3.97h-3.82c.41-1.43 1.08-2.77 1.91-3.97M18.92 8h-2.95a15.65 15.65 0 0 0-1.38-3.56c1.84.63 3.37 1.9 4.33 3.56M12 2C6.47 2 2 6.5 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2z"/></svg>`,
      calendly: `<svg viewBox="2 2 20 20" fill="currentColor"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>`,
      email: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>`,
      whatsapp: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>`
    };
    
    if (!icons[platform]) {
      throw new Error(`Unknown social platform: ${platform}`);
    }
    
    return icons[platform];
  }
</script>

<div 
  class="card-block"
  class:image-left={layout === 'image-left'}
  class:image-right={layout === 'image-right'}
  class:image-top={layout === 'image-top'}
  class:image-background={layout === 'image-background'}
  class:has-background={hasBackground}
  class:extend-background={hasBackground && shouldExtendBackground}
  style:max-width={getMaxWidth()}
  style:--card-text-color={block.textColor}
  style:--card-text-dark-color={block.textDarkColor}
  style={cardStyle}
  use:responsiveStyle={responsiveCSS || {}}
>
  {#if hasMedia && layout !== 'image-background'}
    <div 
      class="card-media"
      class:circular={imageStyle === 'circular'}
      class:rounded={imageStyle === 'rounded'}
      class:full={imageStyle === 'full'}
    >
      {#if mediaForComponent}
        <Media 
          media={mediaForComponent as any} 
          className={imageStyle === 'circular' ? 'circular' : ''} 
        />
      {/if}
    </div>
  {/if}
  
  {#if layout === 'image-background' && hasMedia && block.media?.type === 'image'}
    <div class="card-background">
      <Image
        src={imageSrc}
        alt={block.media?.alt || ''}
        aspectRatio="16:9"
        backgroundSize="cover"
      />
      <div class="card-overlay"></div>
    </div>
  {/if}
  
  <div class="card-content" class:center={block.alignment === 'center'} class:right={block.alignment === 'right'}>
    {#if block.title}
      <h2 class="card-title">{block.title}</h2>
    {/if}
    
    {#if block.subtitle}
      <p class="card-subtitle">{block.subtitle}</p>
    {/if}
    
    {#if block.content}
      <div class="card-description">
        {#if block.markdown}
          <Markdown content={block.content} align={block.alignment || 'left'} color={block.textColor || undefined} inline />
        {:else}
          <Text align={block.alignment || 'left'} inline>{block.content}</Text>
        {/if}
      </div>
    {/if}
    
    {#if block.links && block.links.length > 0}
      {@const allIconOnly = block.links.every(link => !link.label)}
      <div class="card-links" class:icon-only={allIconOnly}>
        {#each block.links as link}
          {@const hasLabel = !!link.label}
          {@const isThemeColor = link.color && ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info'].includes(link.color)}
          <Button
            variant={(link.variant || (hasLabel && isThemeColor ? link.color : hasLabel ? "subtle" : "icon-box")) as any}
            size="sm"
            inline
            color={!isThemeColor ? getThemeColor(link.color) : undefined}
            bgColor={hasLabel && !isThemeColor ? getThemeColor(link.color) : undefined}
            on:click={() => window.open(getLinkUrl(link), '_blank')}
          >
            <span class="social-icon" class:no-label={!hasLabel}>
              {@html getSocialIcon(link.platform)}
            </span>
            {#if link.label}
              <span class="social-label">{link.label}</span>
            {/if}
          </Button>
        {/each}
      </div>
    {/if}
    
    {#if block.cta && block.cta.label}
      <div class="card-cta">
        <Button
          label={block.cta.label}
          variant={(block.cta.variant || 'primary') as any}
          size="lg"
          inline
          on:click={() => {
            if (block.cta) {
              handleCtaNavigation(block.cta, onCtaClick);
            }
          }}
        />
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .card-block {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin: 0 auto;
    padding: var(--card-block-padding, 2.5rem 1rem);
    position: relative;
    container-type: inline-size;
    /* Apply text color if provided */
    color: var(--card-text-color, inherit);
  }
  
  /* Dark mode text color support */
  :global([data-theme='dark']) .card-block {
    color: var(--card-text-dark-color, var(--card-text-color, inherit));
  }
  
  /* Card with background image or gradient */
  .card-block.has-background {
    position: relative;
    isolation: isolate;
  }
  
  /* Extended background using pseudo-element */
  .card-block.extend-background {
    position: relative;
  }
  
  .card-block.extend-background::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 100%;
    z-index: 0;
    /* Background will be applied via CSS variable */
    background: var(--card-background);
  }
  
  /* Keep all content above the extended background */
  .card-block.extend-background > * {
    position: relative;
    z-index: 1;
  }
  
  .card-block.has-background .card-content {
    position: relative;
    z-index: 1;
  }
  
  .card-block.has-background .card-media {
    position: relative;
    z-index: 1;
  }
  
  /* Media container styles */
  .card-media {
    width: 100%;
    overflow: hidden;
  }
  
  /* Circular styling - always circular on both mobile and desktop */
  .card-media.circular {
    width: 200px;
    height: 200px;
    border-radius: 50%;
    margin: 0 auto;
  }
  
  .card-media.circular :global(.image-container),
  .card-media.circular :global(.media-video) {
    border-radius: 50%;
  }
  
  /* Non-circular media extends to full width on mobile - REMOVE ALL PADDING AND BORDER-RADIUS */
  @media (max-width: 767px) {
    .card-block.image-left .card-media:not(.circular),
    .card-block.image-right .card-media:not(.circular),
    .card-block.image-top .card-media:not(.circular) {
      width: 100vw !important; /* Full viewport width */
      margin-left: calc(-50vw + 50%) !important; /* Break out of container */
      margin-top: 0 !important; /* No top margin */
      margin-bottom: 0 !important; /* No bottom margin */
      border-radius: 0 !important; /* NO border-radius */
      padding: 0 !important; /* NO padding */
    }
    
    .card-media:not(.circular) :global(.media-video) {
      width: 100% !important;
      height: 100% !important;
      border-radius: 0 !important; /* NO border-radius */
    }
    
    .card-media:not(.circular) :global(video) {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      border-radius: 0 !important; /* NO border-radius */
    }
    
    /* Remove any inherited border-radius from parent card */
    .card-block:has(.card-media:not(.circular)) {
      border-radius: 0 !important;
      padding-top: 0 !important;
    }
  }

  /* Desktop: subtle border-radius for non-circular */
  @media (min-width: 768px) {
    .card-media:not(.circular) {
      border-radius: 5px !important; /* Subtle 5px border-radius on desktop */
    }
    
    .card-media:not(.circular) :global(.media-video),
    .card-media:not(.circular) :global(video) {
      border-radius: 5px; /* Match container border-radius */
    }
  }
  
  .card-media.rounded {
    border-radius: 1rem;
  }
  
  .card-media.rounded :global(.image-container) {
    border-radius: 1rem;
  }
  
  .card-media.full {
    margin: 0 -1rem;
    width: calc(100% + 2rem);
  }
  
  /* Background image layout */
  .card-block.image-background {
    padding: 0;
    overflow: hidden;
  }
  
  .card-background {
    position: absolute;
    inset: 0;
    z-index: 0;
  }
  
  .card-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%);
  }
  
  .card-block.image-background .card-content {
    position: relative;
    z-index: 1;
    padding: 3rem 2rem;
    color: white;
  }
  
  /* Content styles */
  .card-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .card-title {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
    color: currentColor;
  }
  
  .card-subtitle {
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.4;
    margin: 0;
    opacity: 0.8;
    color: currentColor;
  }
  
  .card-description {
    margin-top: 0.5rem;
    line-height: 1.6;
  }
  
  /* Social links */
  .card-links {
    display: flex;
    flex-wrap: wrap;
    gap: var(--card-links-gap, 0.5rem);
    margin-top: var(--card-links-margin-top, 1rem);
    justify-content: var(--card-links-justify, flex-start);
    width: var(--card-links-width, auto);
  }

  
  .card-links :global(.btn) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  .card-links :global(.btn--icon-box) {
    padding: var(--card-link-btn-padding, 0.375rem) !important;
  }
  
  .social-icon {
    display: flex;
    width: var(--social-icon-size, 18px);
    height: var(--social-icon-size, 18px);
  }

  .social-icon :global(svg) {
    width: 100%;
    height: 100%;
  }

  .social-icon.no-label {
    width: var(--social-icon-size, 18px);
    height: var(--social-icon-size, 18px);
  }
  
  .social-label {
    font-size: 0.875rem;
    font-weight: 500;
  }
  
  /* CTA */
  .card-cta {
    margin-top: 1.5rem;
  }
  
  /* Alignment variations */
  .card-content.center {
    text-align: center;
    align-items: center;
  }
  
  .card-content.center .card-links {
    justify-content: center;
  }
  
  .card-content.right {
    text-align: right;
    align-items: flex-end;
  }
  
  .card-content.right .card-links {
    justify-content: flex-end;
  }
  
  /* Desktop layouts */
  @container (min-width: 768px) {
    .card-block {
      padding: 4rem 1rem;
    }
    .card-block.image-left,
    .card-block.image-right {
      flex-direction: row;
      align-items: center;
      gap: 3rem;
    }
    
    .card-block.image-right {
      flex-direction: row-reverse;
    }
    
    .card-block.image-left .card-media,
    .card-block.image-right .card-media {
      flex: 0 0 250px;
    }
    
    .card-block.image-left .card-media.circular,
    .card-block.image-right .card-media.circular {
      width: 250px;
      height: 250px;
      margin: 0;
    }
    
    .card-block.image-left .card-media.full,
    .card-block.image-right .card-media.full {
      margin: 0;
      width: 250px;
    }
    
    .card-block.image-left .card-content,
    .card-block.image-right .card-content {
      flex: 1;
    }
    
    /* Force left alignment for image-left variant on desktop */
    .card-block.image-left .card-content {
      text-align: left !important;
      align-items: flex-start !important;
    }
    
    .card-block.image-left .card-title,
    .card-block.image-left .card-subtitle {
      text-align: left !important;
    }
    
    /* For description, only apply to the container, not override inline styles */
    .card-block.image-left .card-description {
      display: block;
      width: 100%;
    }
    
    /* Override the markdown alignment for image-left */
    .card-block.image-left .card-description :global(.markdown-container) {
      text-align: left !important;
    }
    
    .card-block.image-left .card-links {
      justify-content: flex-start !important;
    }
    
    .card-block.image-left .card-cta {
      display: flex;
      justify-content: flex-start;
    }
  }
  
</style>